/* ============================================================================
   RequireAuth - signed-out users are redirected to /login.
   ----------------------------------------------------------------------------
   AuthProvider persists the bearer token in localStorage when available, so
   on app load we may briefly be "restoring" — validating the persisted token
   against the backend. While restoring we hold off the redirect so a user who
   IS signed in doesn't briefly land on /login before the session resolves.

   Public routes that should NOT be gated (login/signup/forgot/reset/verify
   pages) are listed in PUBLIC_ROUTES below — those render their children as
   normal regardless of auth state.
   ========================================================================= */
import { ReactNode, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "./AuthProvider";

// Routes that render without the auth gate. Keep in sync with App.tsx.
// Hash-routing means the comparison is against the hash path, not pathname.
const PUBLIC_ROUTES = [
  "/welcome",
  "/login",
  "/signup",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/verify-email",
  "/portal/auth/forgot-password",
  "/portal/auth/reset-password",
  "/portal/auth/verify-email",
];

function isPublicRoute(path: string): boolean {
  // Exact match or sub-path (e.g. /auth/verify-email?token=abc).
  return PUBLIC_ROUTES.some((p) => path === p || path.startsWith(p + "?"));
}

interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { isSignedIn, isRestoring } = useAuth();
  const [loc, setLocation] = useLocation();
  const isPublic = isPublicRoute(loc);

  useEffect(() => {
    if (!isRestoring && !isSignedIn && !isPublic) {
      setLocation("/signup");
    }
  }, [isRestoring, isSignedIn, isPublic, loc, setLocation]);

  // While we're hydrating a persisted session, render a quiet placeholder so
  // we don't flash /login for users who are actually signed in.
  if (isRestoring) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background"
        data-testid="auth-restoring"
      >
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <div className="h-8 w-8 rounded-full border-2 border-border border-t-primary animate-spin" />
          <p className="text-xs uppercase tracking-[0.18em]">Restoring session</p>
        </div>
      </div>
    );
  }

  // While we're redirecting, render nothing so the protected page never
  // flashes its skeleton for a frame.
  if (!isSignedIn && !isPublic) {
    return null;
  }

  return <>{children}</>;
}
